import { DrawerItem, DrawerConfig, DrawerContext, DrawerRoutingRules } from './contexts/drawer-stack-context.js';
import { DrawerDebugger, safeObjectKeys } from './contexts/drawer-stack/debug.js';

// ============================================================================
// DRAWER ROUTER IMPLEMENTATION
// ============================================================================

export class DrawerRouter implements DrawerRoutingRules {
  private nestingMatrix: Record<string, Record<string, boolean>> = {
    'modal': {
      'modal': false,        // Don't nest modals
      'sheet': true,         // Can nest sheets in modals
      'bottom-drawer': true, // Can nest bottom drawers in modals
      'side-drawer': true,   // Can nest side drawers in modals
      'fullscreen': false    // Don't nest fullscreen in modals
    },
    'sheet': {
      'modal': false,        // Don't nest modals in sheets
      'sheet': false,        // Don't nest sheets in sheets
      'bottom-drawer': false, // Don't nest bottom drawers in sheets
      'side-drawer': true,   // Can nest side drawers in sheets
      'fullscreen': false    // Don't nest fullscreen in sheets
    },
    'bottom-drawer': {
      'modal': false,        // Don't nest modals in bottom drawers
      'sheet': false,        // Don't nest sheets in bottom drawers
      'bottom-drawer': false, // Don't nest bottom drawers
      'side-drawer': false,  // Don't nest side drawers in bottom drawers
      'fullscreen': false    // Don't nest fullscreen in bottom drawers
    },
    'side-drawer': {
      'modal': false,        // Don't nest modals in side drawers
      'sheet': true,         // Can nest sheets in side drawers
      'bottom-drawer': false, // Don't nest bottom drawers in side drawers
      'side-drawer': false,  // Don't nest side drawers
      'fullscreen': false    // Don't nest fullscreen in side drawers
    },
    'fullscreen': {
      'modal': false,        // Don't nest modals in fullscreen
      'sheet': true,         // Can nest sheets in fullscreen
      'bottom-drawer': false, // Don't nest bottom drawers in fullscreen
      'side-drawer': true,   // Can nest side drawers in fullscreen
      'fullscreen': false    // Don't nest fullscreen
    }
  };

  private typePreferenceMatrix: Record<string, Record<string, DrawerItem['type'][]>> = {
    'mobile': {
      'simple': ['bottom-drawer', 'side-drawer'],
      'medium': ['bottom-drawer', 'fullscreen'],
      'complex': ['fullscreen']
    },
    'desktop': {
      'simple': ['side-drawer', 'modal'],
      'medium': ['modal', 'sheet'],
      'complex': ['modal', 'fullscreen']
    }
  };

  private contentTypeMatrix: Record<string, DrawerItem['type'][]> = {
    'form': ['modal', 'side-drawer', 'fullscreen'],
    'list': ['side-drawer', 'modal', 'bottom-drawer'],
    'details': ['side-drawer', 'modal'],
    'actions': ['bottom-drawer', 'side-drawer'],
    'navigation': ['bottom-drawer', 'side-drawer']
  };

  canNest(parentType: string, childType: string): boolean {
    if (!parentType || !childType) return false;
    return this.nestingMatrix[parentType]?.[childType] ?? false;
  }

  getOptimalType(context: DrawerContext): DrawerItem['type'] {
    // Validate context first
    const contextIssues = DrawerDebugger.validateContext(context);
    if (contextIssues.length > 0) {
      DrawerDebugger.error(context, new Error(`Invalid context: ${contextIssues.join(', ')}`));
      return 'modal'; // Safe fallback
    }

    DrawerDebugger.log(context, 'getOptimalType called', {
      nestingMatrix: this.nestingMatrix
    });

    // Check if we have a parent drawer and nesting restrictions
    if (context.parentDrawer && context.parentDrawer.type) {
      const parentType = context.parentDrawer.type;
      const nestingOptions = this.nestingMatrix[parentType];
      
      DrawerDebugger.log(context, 'Parent drawer found', {
        parentType,
        nestingOptions,
        nestingMatrixKeys: safeObjectKeys(this.nestingMatrix)
      });
      
      if (nestingOptions && typeof nestingOptions === 'object') {
        try {
          const availableTypes = safeObjectKeys(nestingOptions)
            .filter(type => this.canNest(parentType, type));
          
          DrawerDebugger.log(context, 'Available types for nesting', { availableTypes });
          
          if (availableTypes.length === 0) {
            // If no nesting is allowed, suggest replacement
            return this.getOptimalType({ ...context, parentDrawer: undefined });
          }
        } catch (error) {
          DrawerDebugger.error(context, error as Error, { nestingOptions });
          return 'modal'; // Safe fallback
        }
      } else {
        DrawerDebugger.log(context, 'Nesting options not found or invalid', { nestingOptions });
      }
    }

    // Get device type
    const deviceType = context.isMobile ? 'mobile' : 'desktop';
    
    DrawerDebugger.log(context, 'Device and content analysis', {
      deviceType,
      contentComplexity: context.contentComplexity,
      contentType: context.contentType,
      typePreferenceMatrix: this.typePreferenceMatrix,
      contentTypeMatrix: this.contentTypeMatrix
    });
    
    // Get content complexity preferences with safety checks
    let complexityPreferences: DrawerItem['type'][] = [];
    try {
      const deviceMatrix = this.typePreferenceMatrix[deviceType];
      if (deviceMatrix && context.contentComplexity) {
        complexityPreferences = deviceMatrix[context.contentComplexity] || [];
      }
      DrawerDebugger.log(context, 'Complexity preferences resolved', { complexityPreferences });
    } catch (error) {
      DrawerDebugger.error(context, error as Error, { deviceType, contentComplexity: context.contentComplexity });
      complexityPreferences = [];
    }
    
    // Get content type preferences with safety checks
    let contentTypePreferences: DrawerItem['type'][] = [];
    try {
      if (context.contentType) {
        contentTypePreferences = this.contentTypeMatrix[context.contentType] || [];
      }
      DrawerDebugger.log(context, 'Content type preferences resolved', { contentTypePreferences });
    } catch (error) {
      DrawerDebugger.error(context, error as Error, { contentType: context.contentType });
      contentTypePreferences = [];
    }
    
    // Find intersection of preferences
    const preferences = complexityPreferences.filter(type => 
      contentTypePreferences.includes(type)
    );
    
    // If no intersection, use complexity preferences
    if (preferences.length === 0) {
      return complexityPreferences[0] as DrawerItem['type'];
    }
    
    // Apply user preference if specified
    if (context.userPreference && context.userPreference !== 'auto') {
      const userPrefType = context.userPreference === 'drawer' ? 'side-drawer' : 'modal';
      if (preferences.includes(userPrefType)) {
        return userPrefType;
      }
    }
    
    // Return first preference
    return preferences[0] as DrawerItem['type'];
  }

  shouldReplace(current: DrawerItem, incoming: Omit<DrawerItem, 'level' | 'timestamp'>): boolean {
    // Always replace if incoming drawer is marked as replaceable
    if (incoming.config.replaceable) {
      return true;
    }
    
    // Replace if same type and both are replaceable
    if (current.type === incoming.type && current.config.replaceable && incoming.config.replaceable) {
      return true;
    }
    
    // Replace if current drawer is old (more than 30 seconds)
    const now = Date.now();
    if (now - current.timestamp > 30000) {
      return true;
    }
    
    // Replace if incoming drawer is more complex
    const complexityOrder = ['bottom-drawer', 'side-drawer', 'sheet', 'modal', 'fullscreen'];
    const currentComplexity = complexityOrder.indexOf(current.type);
    const incomingComplexity = complexityOrder.indexOf(incoming.type);
    
    if (incomingComplexity > currentComplexity) {
      return true;
    }
    
    return false;
  }

  getOptimalConfig(type: DrawerItem['type'], context: DrawerContext): DrawerConfig {
    const baseConfig: DrawerConfig = {
      nested: !!context.parentDrawer,
      replaceable: context.contentComplexity === 'simple',
      mobileOptimized: context.isMobile
    };

    switch (type) {
      case 'bottom-drawer':
        return {
          ...baseConfig,
          side: 'bottom',
          height: context.isMobile ? '85vh' : '70vh',
          size: 'full',
          className: 'rounded-t-xl'
        };
      
      case 'side-drawer':
        return {
          ...baseConfig,
          side: context.isMobile ? 'bottom' : 'right',
          width: context.isMobile ? '100vw' : '400px',
          height: context.isMobile ? '85vh' : '100vh',
          size: context.isMobile ? 'full' : 'md'
        };
      
      case 'modal':
        return {
          ...baseConfig,
          side: 'right',
          size: context.contentComplexity === 'complex' ? 'xl' : 'lg',
          width: context.isMobile ? '100vw' : '600px',
          height: context.isMobile ? '100vh' : 'auto'
        };
      
      case 'sheet':
        return {
          ...baseConfig,
          side: 'right',
          size: 'md',
          width: context.isMobile ? '100vw' : '400px',
          height: context.isMobile ? '100vh' : '100vh'
        };
      
      case 'fullscreen':
        return {
          ...baseConfig,
          side: 'right',
          size: 'full',
          width: '100vw',
          height: '100vh',
          nested: false, // Fullscreen drawers shouldn't be nested
          replaceable: false // Fullscreen drawers shouldn't be replaced
        };
      
      default:
        return baseConfig;
    }
  }
}

// ============================================================================
// DRAWER ROUTER INSTANCE
// ============================================================================

export const drawerRouter = new DrawerRouter();

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

export function createDrawerContext(
  isMobile: boolean,
  isTouchDevice: boolean,
  contentComplexity: 'simple' | 'medium' | 'complex',
  contentType: 'form' | 'list' | 'details' | 'actions' | 'navigation',
  screenSize: 'xs' | 'sm' | 'md' | 'lg' | 'xl',
  parentDrawer?: DrawerItem,
  userPreference?: 'drawer' | 'modal' | 'auto'
): DrawerContext {
  return {
    isMobile,
    isTouchDevice,
    contentComplexity,
    contentType,
    parentDrawer,
    screenSize,
    userPreference
  };
}

export function analyzeContentComplexity(
  children: React.ReactNode,
  estimatedFields?: number
): 'simple' | 'medium' | 'complex' {
  // Estimate based on props if provided
  if (estimatedFields) {
    if (estimatedFields <= 3) return 'simple';
    if (estimatedFields <= 7) return 'medium';
    return 'complex';
  }
  
  // Default to medium complexity
  return 'medium';
}

export function detectContentType(
  children: React.ReactNode,
  title?: string,
  actions?: React.ReactNode
): 'form' | 'list' | 'details' | 'actions' | 'navigation' {
  // Simple heuristic based on props
  if (title?.toLowerCase().includes('form') || 
      title?.toLowerCase().includes('create') || 
      title?.toLowerCase().includes('edit')) {
    return 'form';
  }
  
  if (title?.toLowerCase().includes('list') || 
      title?.toLowerCase().includes('search') ||
      title?.toLowerCase().includes('results')) {
    return 'list';
  }
  
  if (title?.toLowerCase().includes('details') || 
      title?.toLowerCase().includes('view') ||
      title?.toLowerCase().includes('info')) {
    return 'details';
  }
  
  if (title?.toLowerCase().includes('actions') || 
      title?.toLowerCase().includes('menu') ||
      actions) {
    return 'actions';
  }
  
  if (title?.toLowerCase().includes('navigation') || 
      title?.toLowerCase().includes('menu')) {
    return 'navigation';
  }
  
  // Default to actions
  return 'actions';
}
